<template>
    <div class="sla-matrix-wrapper">
        <v-tabs
            v-model="activeTab"
            height="36"
            background-color="background-1"
            slider-color="primary"
            class="round-card"
        >
            <v-tab
                v-for="rt in requestTypes"
                :key="rt.value"
                ripple
                class="secondary-tab"
            >
                {{ rt.label || rt.text || rt.value }}
            </v-tab>

            <v-tab-item
                v-for="rt in requestTypes"
                :key="rt.value"
                class="background-1 pt-3"
            >
                <div v-if="requestTypes.length > 1" class="d-flex align-center mb-2">
                    <v-spacer></v-spacer>
                    <v-menu offset-y left>
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn
                                x-small
                                text
                                color="primary"
                                v-bind="attrs"
                                v-on="on"
                            >
                                <v-icon x-small class="mr-1">mdi-content-copy</v-icon>
                                Copia da...
                            </v-btn>
                        </template>
                        <v-list dense>
                            <v-list-item
                                v-for="source in otherRequestTypes(rt.value)"
                                :key="source.value"
                                @click="copyFrom(source.value, rt.value)"
                            >
                                <v-list-item-title>{{ source.label || source.text || source.value }}</v-list-item-title>
                            </v-list-item>
                        </v-list>
                    </v-menu>
                </div>
                <v-simple-table dense class="background-2 round-card">
                    <thead>
                        <tr>
                            <th class="text-left" style="min-width: 150px;">
                                Priorità
                            </th>
                            <th class="text-center" style="min-width: 180px;">
                                Risposta
                            </th>
                            <th class="text-center" style="min-width: 180px;">
                                Risoluzione
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="priority in priorities"
                            :key="priority.value"
                        >
                            <td style="white-space: nowrap;">
                                <div class="d-flex align-center">
                                    <span
                                        class="mr-2 rounded-circle d-inline-block"
                                        :style="{
                                            width: '10px',
                                            height: '10px',
                                            backgroundColor: priorityColor(priority.value),
                                            flexShrink: 0
                                        }"
                                    ></span>
                                    <span class="subtitle-2 font-weight-medium">
                                        {{ priority.label || priority.text || priority.value }}
                                    </span>
                                    <v-menu v-if="priorities.length > 1" offset-y left>
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-btn
                                                x-small
                                                icon
                                                v-bind="attrs"
                                                v-on="on"
                                                class="ml-1"
                                            >
                                                <v-icon x-small color="grey">mdi-content-copy</v-icon>
                                            </v-btn>
                                        </template>
                                        <v-list dense>
                                            <v-list-item
                                                v-for="source in otherPriorities(priority.value)"
                                                :key="source.value"
                                                @click="copyFromPriority(source.value, priority.value, rt.value)"
                                            >
                                                <v-list-item-title>{{ source.label || source.text || source.value }}</v-list-item-title>
                                            </v-list-item>
                                        </v-list>
                                    </v-menu>
                                </div>
                            </td>

                            <!-- Risposta -->
                            <td>
                                <div class="d-flex align-center justify-center" style="gap: 6px;">
                                    <v-text-field
                                        :value="getCellValue(priority.value, rt.value, 'response_hours')"
                                        @input="setCellValue(priority.value, rt.value, 'response_hours', $event)"
                                        type="number"
                                        min="0"
                                        dense
                                        outlined
                                        hide-details
                                        suffix="h"
                                        style="max-width: 80px;"
                                        class="sla-cell-input"
                                    ></v-text-field>
                                    <v-text-field
                                        :value="getCellValue(priority.value, rt.value, 'response_minutes')"
                                        @input="setCellValue(priority.value, rt.value, 'response_minutes', $event)"
                                        type="number"
                                        min="0"
                                        max="59"
                                        dense
                                        outlined
                                        hide-details
                                        suffix="m"
                                        style="max-width: 80px;"
                                        class="sla-cell-input"
                                    ></v-text-field>
                                </div>
                            </td>

                            <!-- Risoluzione -->
                            <td>
                                <div class="d-flex align-center justify-center" style="gap: 6px;">
                                    <v-text-field
                                        :value="getCellValue(priority.value, rt.value, 'resolution_hours')"
                                        @input="setCellValue(priority.value, rt.value, 'resolution_hours', $event)"
                                        type="number"
                                        min="0"
                                        dense
                                        outlined
                                        hide-details
                                        suffix="h"
                                        style="max-width: 80px;"
                                        class="sla-cell-input"
                                    ></v-text-field>
                                    <v-text-field
                                        :value="getCellValue(priority.value, rt.value, 'resolution_minutes')"
                                        @input="setCellValue(priority.value, rt.value, 'resolution_minutes', $event)"
                                        type="number"
                                        min="0"
                                        max="59"
                                        dense
                                        outlined
                                        hide-details
                                        suffix="m"
                                        style="max-width: 80px;"
                                        class="sla-cell-input"
                                    ></v-text-field>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </v-simple-table>
            </v-tab-item>
        </v-tabs>
    </div>
</template>

<script>
const PRIORITY_COLORS = {
    very_high: '#f44336',
    high: '#ff9800',
    medium: '#2196f3',
    low: '#4caf50',
    molto_alta: '#f44336',
    alta: '#ff9800',
    media: '#2196f3',
    bassa: '#4caf50'
}

export default {
    name: 'SlaMatrix',

    model: {
        prop: 'value',
        event: 'input'
    },

    props: {
        value: {
            type: Object,
            default: () => ({})
        },
        priorities: {
            type: Array,
            default: () => []
        },
        requestTypes: {
            type: Array,
            default: () => []
        }
    },

    data() {
        return {
            activeTab: 0
        }
    },

    computed: {
        matrix() {
            return this.value || {}
        }
    },

    methods: {
        getCellValue(priorityValue, rtValue, field) {
            const cell = (this.matrix[priorityValue] || {})[rtValue] || {}
            const val = cell[field]
            return val !== undefined ? val : 0
        },

        setCellValue(priorityValue, rtValue, field, rawValue) {
            const parsed = parseInt(rawValue, 10)
            const value = isNaN(parsed) || parsed < 0 ? 0 : parsed

            const updated = JSON.parse(JSON.stringify(this.matrix))

            if (!updated[priorityValue]) {
                this.$set(updated, priorityValue, {})
            }
            if (!updated[priorityValue][rtValue]) {
                this.$set(updated[priorityValue], rtValue, {
                    response_hours: 0,
                    response_minutes: 0,
                    resolution_hours: 0,
                    resolution_minutes: 0
                })
            }
            updated[priorityValue][rtValue][field] = value
            this.$emit('input', updated)
        },

        otherRequestTypes(currentRtValue) {
            return this.requestTypes.filter(rt => rt.value !== currentRtValue)
        },

        otherPriorities(currentPriorityValue) {
            return this.priorities.filter(p => p.value !== currentPriorityValue)
        },

        copyFromPriority(sourcePriority, targetPriority, rtValue) {
            const updated = JSON.parse(JSON.stringify(this.matrix))
            const sourceCell = (updated[sourcePriority] || {})[rtValue] || {
                response_hours: 0,
                response_minutes: 0,
                resolution_hours: 0,
                resolution_minutes: 0
            }
            if (!updated[targetPriority]) updated[targetPriority] = {}
            updated[targetPriority][rtValue] = JSON.parse(JSON.stringify(sourceCell))
            this.$emit('input', updated)
        },

        copyFrom(sourceRtValue, targetRtValue) {
            const updated = JSON.parse(JSON.stringify(this.matrix))
            this.priorities.forEach(p => {
                const sourceCell = (updated[p.value] || {})[sourceRtValue] || {
                    response_hours: 0,
                    response_minutes: 0,
                    resolution_hours: 0,
                    resolution_minutes: 0
                }
                if (!updated[p.value]) updated[p.value] = {}
                updated[p.value][targetRtValue] = JSON.parse(JSON.stringify(sourceCell))
            })
            this.$emit('input', updated)
        },

        priorityColor(priorityValue) {
            return PRIORITY_COLORS[priorityValue] || '#9e9e9e'
        },

        toApiTargets() {
            const targets = []
            this.priorities.forEach(p => {
                this.requestTypes.forEach(rt => {
                    const cell = (this.matrix[p.value] || {})[rt.value] || {}
                    const rh = parseInt(cell.response_hours || 0, 10)
                    const rm = parseInt(cell.response_minutes || 0, 10)
                    const resH = parseInt(cell.resolution_hours || 0, 10)
                    const resM = parseInt(cell.resolution_minutes || 0, 10)
                    targets.push({
                        priority: p.value,
                        request_type: rt.value,
                        response_time_minutes: rh * 60 + rm,
                        resolution_time_minutes: resH * 60 + resM
                    })
                })
            })
            return targets
        }
    }
}
</script>

<style scoped>
.sla-cell-input >>> .v-input__control {
    min-height: 32px !important;
}
.sla-cell-input >>> .v-text-field__slot input {
    font-size: 13px;
    padding: 4px 0;
}
.sla-matrix-wrapper table th,
.sla-matrix-wrapper table td {
    padding: 8px 12px !important;
}
</style>
